/**
 * Named colour scales shared across the app.
 *
 * These are the same scales offered by the density report. Each scale is a
 * hue sweep from `sHue` to `eHue` (HSL hue degrees). The density report sends
 * the start/end hue to the backend, while the frontend can sample the sweep
 * into a discrete set of colours (e.g. for a SOG legend ramp).
 *
 * This module is the single source of truth — both the density report mapping
 * and the vector-track colouring derive from `COLOR_SCALES`.
 */

export interface NamedColorScale {
  /** Backend identifier for the scale. */
  id: number;
  /** Stable string key used as the select value. */
  value: string;
  /** Human-readable label. */
  label: string;
  /** Start hue in degrees (0–360). */
  sHue: number;
  /** End hue in degrees (0–360). */
  eHue: number;
}

export const COLOR_SCALES: NamedColorScale[] = [
  { id: 0, value: 'yellow to red', label: 'yellow to red', sHue: 60, eHue: 0 },
  {
    id: 1,
    value: 'green, yellow to red',
    label: 'green, yellow to red',
    sHue: 120,
    eHue: 0,
  },
  {
    id: 2,
    value: 'yellow to green',
    label: 'yellow to green',
    sHue: 60,
    eHue: 120,
  },
  {
    id: 3,
    value: 'lightblue to blue',
    label: 'lightblue to blue',
    sHue: 180,
    eHue: 240,
  },
  {
    id: 4,
    value: 'purple to blue',
    label: 'purple to blue',
    sHue: 300,
    eHue: 240,
  },
  {
    id: 5,
    value: 'purple to red',
    label: 'purple to red',
    sHue: 300,
    eHue: 360,
  },
  {
    id: 7,
    value: 'green to teal',
    label: 'green to teal',
    sHue: 120,
    eHue: 180,
  },
  {
    id: 8,
    value: 'green, red, green, teal, blue to purple',
    label: 'green, red, green, teal, blue to purple',
    sHue: 0,
    eHue: 300,
  },
  {
    id: 9,
    value: 'yellow to teal',
    label: 'yellow to teal',
    sHue: 60,
    eHue: 180,
  },
];

export const DEFAULT_COLOR_SCALE = COLOR_SCALES[1]; // green, yellow to red — sequential green→red ramp

export function getColorScaleByValue(value: string): NamedColorScale {
  return COLOR_SCALES.find((s) => s.value === value) ?? COLOR_SCALES[0];
}

export function getColorScaleByHues(
  sHue: number,
  eHue: number,
): NamedColorScale | undefined {
  return COLOR_SCALES.find((s) => s.sHue === sHue && s.eHue === eHue);
}

/**
 * Sample a colour scale into `count` discrete CSS colours by interpolating the
 * hue linearly from `sHue` to `eHue`. Returns `hsl(...)` strings.
 */
export function sampleColorScale(
  value: string,
  count: number,
  saturation = 0.8,
  lightness = 0.45,
): string[] {
  if (count <= 0) return [];
  const { sHue, eHue } = getColorScaleByValue(value);
  const s = Math.round(saturation * 100);
  const l = Math.round(lightness * 100);

  if (count === 1) return [`hsl(${sHue}, ${s}%, ${l}%)`];

  return Array.from({ length: count }, (_, i) => {
    const t = i / (count - 1);
    const hue = Math.round(sHue + (eHue - sHue) * t);
    return `hsl(${((hue % 360) + 360) % 360}, ${s}%, ${l}%)`;
  });
}
